import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Circle, ArrowRight, Sparkles, PartyPopper } from 'lucide-react';
import { useChecklist } from '../../lib/useChecklist';
import type { ChecklistItem } from '../../lib/types';

export default function GettingStartedPage() {
  const { data, loading, toggle, allDone } = useChecklist();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="h-5 w-5 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const pct = data.totalItems > 0 ? Math.round((data.completedCount / data.totalItems) * 100) : 0;
  const autoItems = data.items.filter((i) => i.auto);
  const manualItems = data.items.filter((i) => !i.auto);

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Encabezado */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-ui-accent" />
          <span className="text-sm font-medium text-ui-accent">Primeros pasos</span>
        </div>
        <h1 className="font-display text-2xl font-semibold text-ui-text">
          Deja tu sitio al 100%
        </h1>
        <p className="mt-1 text-sm text-ui-text-muted">
          Completa estas tareas para que tu sitio esté listo para recibir clientes.
        </p>
      </div>

      {/* Barra de progreso general */}
      <div className="rounded-2xl border border-ui-border bg-ui-surface p-5 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-ui-text">Progreso general</span>
          <span className="text-sm font-semibold text-ui-accent">{pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-ui-text/10 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="h-full rounded-full bg-ui-accent"
          />
        </div>
        <p className="text-sm text-ui-text-subtle">
          {data.completedCount} de {data.totalItems} tareas completadas
        </p>

        {allDone && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2 pt-1"
          >
            <PartyPopper className="h-4 w-4 text-ui-accent" />
            <span className="text-sm font-medium text-ui-text">
              ¡Felicidades! Tu sitio está completamente configurado.
            </span>
          </motion.div>
        )}
      </div>

      {/* Tareas automáticas */}
      <Section title="Se detectan automáticamente" hint="Las marcamos nosotros cuando las completas en el panel.">
        {autoItems.map((item, i) => (
          <AutoItem key={item.id} item={item} delay={i * 0.06} onNavigate={() => item.href && navigate(item.href)} />
        ))}
      </Section>

      {/* Tareas manuales */}
      <Section title="Las marcas tú" hint="Avísanos cuando las hayas hecho para llevar el conteo.">
        {manualItems.map((item, i) => (
          <ManualItem key={item.id} item={item} delay={i * 0.06} onToggle={(completed) => toggle(item.id, completed)} />
        ))}
      </Section>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-3">
        <p className="text-sm font-medium text-ui-text">{title}</p>
        <p className="text-sm text-ui-text-subtle">{hint}</p>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function AutoItem({ item, delay, onNavigate }: { item: ChecklistItem; delay: number; onNavigate: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay }}
      className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
        item.completed
          ? 'bg-ui-surface border-ui-border'
          : 'bg-ui-surface border-ui-border hover:border-ui-border-strong'
      }`}
    >
      <span className="flex-shrink-0 mt-0.5">
        {item.completed ? (
          <CheckCircle2 className="h-5 w-5 text-ui-accent" />
        ) : (
          <Circle className="h-5 w-5 text-ui-text/20" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${item.completed ? 'text-ui-text-muted line-through' : 'text-ui-text'}`}>
          {item.label}
        </p>
        <p className="text-sm text-ui-text-subtle mt-0.5 leading-snug">{item.hint}</p>
      </div>
      {!item.completed && item.href && (
        <button
          type="button"
          onClick={onNavigate}
          className="flex-shrink-0 flex items-center gap-1 text-sm text-ui-accent hover:opacity-80 transition-opacity font-medium"
        >
          Ir <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </motion.div>
  );
}

function ManualItem({
  item, delay, onToggle,
}: {
  item: ChecklistItem; delay: number; onToggle: (completed: boolean) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay }}
      className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer select-none transition-colors ${
        item.completed
          ? 'bg-ui-surface border-ui-border'
          : 'bg-ui-surface border-ui-border hover:border-ui-border-strong'
      }`}
      onClick={() => onToggle(!item.completed)}
      role="checkbox"
      aria-checked={item.completed}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(!item.completed); } }}
    >
      <span className="flex-shrink-0 mt-0.5">
        {item.completed ? (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 16 }}>
            <CheckCircle2 className="h-5 w-5 text-ui-accent" />
          </motion.span>
        ) : (
          <Circle className="h-5 w-5 text-ui-text/20" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${item.completed ? 'text-ui-text-muted line-through' : 'text-ui-text'}`}>
          {item.label}
        </p>
        <p className="text-sm text-ui-text-subtle mt-0.5 leading-snug">{item.hint}</p>
      </div>
      {item.href && !item.completed && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); window.location.href = item.href!; }}
          className="flex-shrink-0 flex items-center gap-1 text-sm text-ui-accent hover:opacity-80 transition-opacity font-medium"
        >
          Ir <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </motion.div>
  );
}
